import gsap from 'gsap';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
import $ from '../core/Dom';
import Viewport from '../core/Viewport';
import Dispatch from '../core/Dispatch';
import {
    COMPONENT_INIT,
    HEADER_HIDDEN,
    HEADER_VISIBLE,
    MENU_CLOSED,
    MENU_OPENING,
    PROGRAMMATIC_SCROLL_END,
    PROGRAMMATIC_SCROLL_START
} from '../lib/events';
import { getMaxScroll } from '../lib/helpers';

gsap.registerPlugin(ScrollToPlugin);

export default (el, props) => {

    const $el = $(el);
    const inner = el.firstElementChild;
    const { sticky: canBeSticky = true } = props || {};
    const isPermanentlySticky = canBeSticky && inner.classList.contains('fixed');
    
    const searchForm = el.querySelector('form[role="search"]');
    let searchInput = null;
    let searchButton = null;
    let searchInputWidth = null;

    let {
        scrollTop: prevScrollTop,
        width: viewW,
        height: viewH
    } = Viewport;

    let height = $(inner).height();
    let { top: offsetTop } = $el.offset();
    let maxScroll = getMaxScroll();

    let isSticky = isPermanentlySticky;
    let isHidden = false;
    let menuIsOpen = false;
    let preventShow = false;

    let mutationObserver;
    let scrollRaf;

    const show = (tween = true) => {
        if (!isHidden) {
            return;
        }
        isHidden = false;
        gsap.killTweensOf(inner);
        if (tween) {
            gsap.timeline()
                .to(inner, {
                    yPercent: 0,
                    duration: 0.3
                }, 0);
        } else {
            gsap.set(inner, { yPercent: 0 });
        }
        Dispatch.emit(HEADER_VISIBLE);
    };

    const hide = (tween = true) => {
        if (isHidden) {
            return;
        }
        isHidden = true;
        gsap.killTweensOf(inner);
        if (tween) {
            gsap.to(inner, {
                yPercent: -100,
                duration: 0.3
            });
        } else {
            gsap.set(inner, { yPercent: -100 });
        }
        Dispatch.emit(HEADER_HIDDEN);
    };

    const stick = () => {
        if (isSticky || isPermanentlySticky) {
            return;
        }
        isSticky = true;
        inner.classList.replace('absolute', 'fixed');
        gsap.set(inner, { top: offsetTop });
    };

    const unstick = () => {
        if (!isSticky || isPermanentlySticky) {
            return;
        }
        isSticky = false;
        inner.classList.replace('fixed', 'absolute');
        gsap.set(inner, { clearProps: 'top' });
    };

    const onScroll = (tween = true, force = false) => {
        const { scrollY: scrollTop } = window;
        let direction = 'up';
        if (prevScrollTop) {
            direction = scrollTop > prevScrollTop ? 'down' : 'up';
        }
        // Make sticky?
        const top = Math.round(scrollTop);
        if (isSticky) {
            if ((isHidden && top <= height) || top <= 0) {
                unstick();
                show(false);
                return;
            }
            if (menuIsOpen) {
                show(false);
            }
            if (!force && Math.abs(top - prevScrollTop) <= 20) {
                return;
            }
            const threshold = isPermanentlySticky ? (viewH / 3) : height;
            if (direction === 'down' && top >= threshold) {
                hide(tween);
            } else if (direction === 'up' && scrollTop < maxScroll && !preventShow) {
                show(tween);
            }
        } else if (top >= height) {
            stick();
            hide(false);
            return;
        }
        prevScrollTop = scrollTop;
    };

    const onResize = (updateScroll = true, force = false) => {
        if (!force && Viewport.width === viewW && Math.abs(Viewport.height - viewH) < 150) { // && getMaxScroll() === maxScroll
            return;
        }

        viewW = Viewport.width;
        viewH = Viewport.height;
        height = $(inner).height();
        offsetTop = $el.offset().top;
        maxScroll = getMaxScroll();
        if (updateScroll) {
            onScroll(force);
        }

        if (searchInput && searchInput.focused) {
            gsap.killTweensOf(searchInput);
            gsap.set(searchInput, { clearProps: 'width,opacity' });
            searchInputWidth = searchInput.getBoundingClientRect().width;
        }
    };

    const scrollHandler = () => {
        if (scrollRaf) {
            cancelAnimationFrame(scrollRaf);
        }
        scrollRaf = requestAnimationFrame(() => {
            scrollRaf = null;
            onScroll();
        });
    };

    const onMenuOpening = () => {
        menuIsOpen = true;
        show(false);
        if (!isSticky && Viewport.scrollTop > 0) {
            gsap.to(window, {
                scrollTo: 0,
                duration: 0.3
            });
        }
    };

    const onMenuClosed = () => {
        setTimeout(() => {
            menuIsOpen = false;
        }, 100);
    };

    const onProgrammaticScrollStart = () => {
        if (!isHidden || !isSticky) {
            return;
        }
        preventShow = true;
    };

    const onProgrammaticScrollEnd = () => {
        preventShow = false;
    };
    
    const onSearchFocusIn = e => {
        if (e.currentTarget.contains(e.relatedTarget)) {
            return;
        }

        const { width: inputWidth } = searchInput.getBoundingClientRect();
        const { width: buttonWidth } = searchButton.getBoundingClientRect();
        gsap.timeline()
            .to(searchInput, { opacity: 1, duration: 0.15 }, 0)
            .fromTo(searchInput, { width: buttonWidth }, { width: inputWidth, duration: 0.3, ease: 'Quad.easeInOut' }, 0)
            .set(searchInput, { clearProps: 'width' });
        searchInputWidth = inputWidth;
    };

    const onSearchFocusOut = e => {
        if (!searchInputWidth || e.currentTarget.contains(e.relatedTarget)) {
            return;
        }
        const { width: buttonWidth } = searchButton.getBoundingClientRect();
        gsap.killTweensOf(searchInput);
        gsap.timeline()
            .fromTo(searchInput, { width: searchInputWidth }, { width: buttonWidth, duration: 0.3, ease: 'Power4.easeIn' }, 0)
            .fromTo(searchInput, { opacity: 1 }, { opacity: 0, duration: 0.15, ease: 'Cubic.easeIn' }, 0.1)
            .set(searchInput, { clearProps: 'width,opacity' });
    };

    const destroy = () => {

        Dispatch.off(MENU_OPENING, onMenuOpening);
        Dispatch.off(MENU_CLOSED, onMenuClosed);

        if (!canBeSticky) {
            return;
        }

        $(el).off('focusin click');

        window.removeEventListener('scroll', scrollHandler);
        Viewport.off('resize', onResize);

        Dispatch.off(PROGRAMMATIC_SCROLL_START, onProgrammaticScrollStart);
        Dispatch.off(PROGRAMMATIC_SCROLL_END, onProgrammaticScrollEnd);

        if (mutationObserver) {
            mutationObserver.disconnect();
            mutationObserver = null;
        }

        if (searchForm) {
            searchForm.removeEventListener('focusin', onSearchFocusIn);
            searchForm.removeEventListener('focusout', onSearchFocusOut);
        }
    };

    const init = () => {

        Dispatch.emit(COMPONENT_INIT);

        Dispatch.on(MENU_OPENING, onMenuOpening);
        Dispatch.on(MENU_CLOSED, onMenuClosed);

        if (!canBeSticky) {
            return;
        }

        $(el).on('focusin', 'a,button', show);

        window.addEventListener('scroll', scrollHandler);
        Viewport.on('resize', onResize);

        onResize(false, true);
        onScroll(false, true);

        Dispatch.on(PROGRAMMATIC_SCROLL_START, onProgrammaticScrollStart);
        Dispatch.on(PROGRAMMATIC_SCROLL_END, onProgrammaticScrollEnd);

        if (window.MutationObserver) {
            mutationObserver = new MutationObserver(() => {
                onResize();
            });
            mutationObserver.observe(document.body, {
                attributes: false,
                childList: true,
                subtree: true
            });
        }
        
        if (searchForm) {
            searchInput = searchForm.querySelector('input');
            searchButton = searchForm.querySelector('button');
            searchForm.addEventListener('focusin', onSearchFocusIn);
            searchForm.addEventListener('focusout', onSearchFocusOut);
        }

    };

    return {
        init,
        destroy
    };

};
